import React, { useState } from 'react'
import './UserSettingPopup.scss'
import '../../../views/pages/census/components/ConfirmationModals/ConfirmModal.scss'
import {
  CButton,
  CButtonGroup,
  CFormCheck,
  CFormSelect,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
} from '@coreui/react-pro'

import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { userSettingPut, userSettingGet } from 'src/redux/user/userThunk'
import { getTheme, setTheme } from '../../../redux/user/userSlice'
import { isTestFeature } from '../../../utils/sharedFeaturesFunctions'
import { BetterToggleSwitch } from '../Toggle/ToggleSwitch'

// makes sure a value is not undefined or null
function hasRealValue(val) {
  return val !== undefined && val !== null
}

// take the user permissions, and determines what default screen options they can choose from
const makeOptions = (permissions) => {
  let options = []
  if (permissions?.includes('Dashboard Tasks View')) {
    options.push({ label: 'Dashboard', value: 'dashboard' })
  }
  if (permissions?.includes('Census view')) {
    options.push({ label: 'Census', value: 'census' })
    options.push({ label: 'Encounters', value: 'encounters' })
  }
  if (permissions?.includes('PracticeIQ - Provider encounters')) {
    options.push({ label: 'Practice IQ', value: 'practiceIq' })
  }
  if (permissions?.includes('claimocity providerhold')) {
    options.push({ label: 'Provider Hold', value: 'providerhold' })
  }
  return options
}

export default function UserSettingPopup({ visible, setUserSettingPopupModel }) {
  const dispatch = useDispatch()
  const theme = useSelector(getTheme)
  const settings = useSelector((state) => state.user?.settings)
  const permissions = useSelector((state) => state.user?.permissions)
  const options = makeOptions(permissions || [])

  const [newHomepage, setNewHomepage] = useState(null)
  const [newCensusDefaultDate, setNewCensusDefaultDate] = useState(null)
  const [newPCCDosFilter, setNewPCCDosFilter] = useState(null)
  const [newTheme, setNewTheme] = useState(null)

  const handleVisit = () => {
    let newUserSettings = {
      ...settings,
      ...(newHomepage && { homepage: newHomepage }),
      ...(newCensusDefaultDate && { censusDefaultDate: newCensusDefaultDate }),
      ...(hasRealValue(newPCCDosFilter) && { pccDosFilter: newPCCDosFilter }),
      ...(newTheme && { theme: newTheme }),
    }

    if (Object.keys(newUserSettings)?.length > 1) {
      dispatch(userSettingPut(newUserSettings)).then(() =>
        dispatch(userSettingGet()).then((data) => {
          setUserSettingPopupModel(false)
        }),
      )
    }
  }

  const onCancel = () => {
    setNewHomepage(null)
    setNewCensusDefaultDate(null)
    setNewPCCDosFilter(null)
    setNewTheme(null)
    setUserSettingPopupModel(false)
  }

  return (
    <CModal
      className="confirm-modal user-settings-container"
      visible={visible}
      onClose={onCancel}
      fullscreen="lg"
      alignment={'center'}
    >
      <CModalHeader className="d-flex">User Settings</CModalHeader>
      <CModalBody className={'settings-body'}>
        <div>
          <CFormSelect
            value={newHomepage || settings?.homepage || 'dashboard'}
            onChange={(e) => {
              setNewHomepage(e?.target?.value)
            }}
            label="Home Page"
            options={options}
          />
        </div>
        <div className="setting-toggle">
          <CFormSelect
            value={newCensusDefaultDate || settings?.censusDefaultDate || 'defaultTODAY'}
            onChange={(e) => {
              setNewCensusDefaultDate(e?.target?.value)
            }}
            label="Census Default"
            options={[
              { label: "Today's Date", value: 'defaultTODAY' },
              { label: 'Last Date Selected', value: 'defaultLAST' },
            ]}
          />
        </div>
        <div className="setting-toggle pccDos d-flex align-items-center justify-content-between">
          <span>
            PCC Copy Data by DOS
            <i
              title={
                'Clinical data pulled into progress notes from PointClickCare (Allergies, Vitals, etc.) will now be based on the data available closest to the date of service.'
              }
              className="fa-regular fa-circle-info pointer"
              style={{
                color: 'var(--default-text-color)',
                marginLeft: '4px',
              }}
            ></i>
          </span>
          <BetterToggleSwitch
            onToggleChange={(e) => {
              setNewPCCDosFilter(e?.currentTarget?.checked)
            }}
            isToggledOn={newPCCDosFilter ?? settings?.pccDosFilter ?? false}
          />
        </div>
        {isTestFeature() && (
          <div className="site-theme">
            <span>Color Theme</span>
            <CButtonGroup aria-label="Theme switch">
              <CFormCheck
                type="radio"
                button={{
                  color: 'primary',
                  shape: (newTheme || theme) === 'light' ? 'selected' : 'unselected',
                }}
                name="theme-switch"
                id="btn-light-theme"
                autoComplete="off"
                label={<i className="fa-regular fa-sun-bright"></i>}
                checked={(newTheme || theme) === 'light'}
                onChange={() => {
                  setNewTheme('light')
                  dispatch(setTheme('light'))
                }}
              />
              <CFormCheck
                type="radio"
                button={{
                  color: 'primary',
                  shape: (newTheme || theme) === 'dark' ? 'selected' : 'unselected',
                }}
                name="theme-switch"
                id="btn-dark-theme"
                autoComplete="off"
                label={<i className="fa-regular fa-moon fa-rotate-by"></i>}
                checked={(newTheme || theme) === 'dark'}
                onChange={() => {
                  setNewTheme('dark')
                  dispatch(setTheme('dark'))
                }}
              />
            </CButtonGroup>
          </div>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onCancel}>
          Cancel
        </CButton>
        <CButton color="primary" onClick={handleVisit}>
          Confirm
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

UserSettingPopup.propTypes = {
  visible: PropTypes.bool,
  setUserSettingPopupModel: PropTypes.func,
}
